/**
 * Видео-референсы: список (спека §12). Фильтры (статус, площадка) живут в адресе;
 * пока есть референсы в работе, список опрашивается раз в 3 с (`useVideoRefs`).
 */
import { Film, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { AddRefDialog } from "@/components/videoref/AddRefDialog";
import { btnPrimary, Empty, ErrorLine, Loading, Segmented } from "@/components/trends/ui";
import { thumbUrl, when } from "@/lib/media";
import {
  fmtTime,
  inProgress,
  platformLabel,
  STATUS_LABEL,
  type VideoRefRow,
  type YtdlpInfo,
} from "@/lib/videoRef";
import { useVideoRefs } from "@/lib/videoRefApi";

type StatusFilter = "all" | "ready" | "work" | "failed";
type PlatformFilter = "all" | "instagram" | "tiktok" | "pinterest" | "youtube" | "file";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "все" },
  { value: "ready", label: "готовые" },
  { value: "work", label: "в работе" },
  { value: "failed", label: "ошибки" },
];

const PLATFORM_OPTIONS: { value: PlatformFilter; label: string }[] = [
  { value: "all", label: "все" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "pinterest", label: "Pinterest" },
  { value: "youtube", label: "YouTube" },
  { value: "file", label: "файл" },
];

function ytdlpLine(y: YtdlpInfo) {
  if (y.error) {
    return <span className="text-err">yt-dlp не установлен: ссылки не работают, файлы — да</span>;
  }
  if (!y.version) return null;
  return (
    <span className={y.stale ? "text-warn" : "text-fg-faint"}>
      yt-dlp {y.version}
      {y.stale ? " — старше 90 дней, обновите" : ""}
    </span>
  );
}

function statusMatches(row: VideoRefRow, f: StatusFilter): boolean {
  if (f === "ready") return row.status === "ready";
  if (f === "work") return inProgress(row.status);
  if (f === "failed") return row.status === "failed";
  return true;
}

function Tile({ row }: { row: VideoRefRow }) {
  const live = inProgress(row.status);
  const failed = row.status === "failed";
  const chip = live
    ? "animate-pulse border-accent/40 bg-accent/10 text-accent-text"
    : failed
      ? "border-err/30 bg-err-bg text-err"
      : "border-border bg-bg-subtle text-fg-muted";
  return (
    <Link
      to={`/video-refs/${row.id}`}
      data-annot={`видео-референс ${row.id}`}
      className="grid content-start gap-1.5 rounded-[var(--radius-card)] border border-border bg-bg p-2 hover:bg-bg-hover"
    >
      <div className="relative aspect-[9/16] overflow-hidden rounded-[4px] bg-bg-subtle">
        {row.poster_path ? (
          <img src={thumbUrl(row.poster_path)} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-fg-faint">
            <Film size={24} />
          </div>
        )}
        <span className="absolute left-1.5 top-1.5 rounded-[3px] bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
          {platformLabel(row.platform)}
        </span>
        {row.duration_ms ? (
          <span className="absolute bottom-1.5 right-1.5 rounded-[3px] bg-black/60 px-1.5 py-0.5 text-[11px] text-white">
            {fmtTime(row.duration_ms).replace(/\.\d$/, "")}
          </span>
        ) : null}
      </div>
      <div className="truncate text-[12.5px] font-medium text-fg" title={row.title ?? row.uploader ?? ""}>
        {row.title || row.uploader || `Референс ${row.id}`}
      </div>
      <div className="text-[11.5px] text-fg-muted">
        {row.scenes} сцен · отмечено {row.marked}
        {row.projects > 0 ? ` · проектов: ${row.projects}` : ""}
      </div>
      <div className="flex items-center gap-1.5">
        <span
          title={failed ? (row.error ?? undefined) : undefined}
          className={`rounded-[3px] border px-1.5 py-0.5 text-[11px] ${chip}`}
        >
          {STATUS_LABEL[row.status]}
        </span>
        <span className="ml-auto truncate text-[11px] text-fg-faint">
          {row.author_name ? `${row.author_name} · ` : ""}
          {when(row.date_create)}
        </span>
      </div>
    </Link>
  );
}

export function VideoRefsPage() {
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const status = (params.get("status") as StatusFilter) || "all";
  const platform = (params.get("platform") as PlatformFilter) || "all";

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  // «в работе» — три статуса, режем на клиенте; готовые и ошибки отбирает сервер.
  const serverStatus = status === "ready" || status === "failed" ? status : undefined;
  // «файл» — площадки нет (NULL): сервер такой фильтр не знает, режем на клиенте.
  const serverPlatform = platform === "all" || platform === "file" ? undefined : platform;
  const list = useVideoRefs({
    ...(serverStatus && { status: serverStatus }),
    ...(serverPlatform && { platform: serverPlatform }),
  });
  const items = (list.data?.items ?? []).filter(
    (r) => statusMatches(r, status) && (platform !== "file" || r.platform === null),
  );

  return (
    <div className="grid content-start gap-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-fg">Видео-референсы</h1>
        <button type="button" className={btnPrimary} onClick={() => setAdding(true)} data-annot="добавить видео-референс">
          <Plus size={14} /> Добавить
        </button>
        <span className="ml-auto text-[12px]" data-annot="версия yt-dlp">
          {list.data ? ytdlpLine(list.data.ytdlp) : null}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Segmented<StatusFilter>
          label="Статус"
          options={STATUS_OPTIONS}
          value={status}
          onPick={(v) => set("status", v)}
          annot="фильтр статуса видео-референсов"
        />
        <Segmented<PlatformFilter>
          label="Площадка"
          options={PLATFORM_OPTIONS}
          value={platform}
          onPick={(v) => set("platform", v)}
          annot="фильтр площадки видео-референсов"
        />
      </div>

      {list.isPending ? <Loading /> : null}
      {list.isError ? <ErrorLine error={list.error} /> : null}
      {list.data && items.length === 0 ? (
        <Empty>
          {status === "all" && platform === "all"
            ? "Референсов пока нет — вставьте ссылку на ролик или загрузите файл"
            : "По этим фильтрам ничего нет"}
        </Empty>
      ) : null}
      {items.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3" data-annot="плитки видео-референсов">
          {items.map((r) => (
            <Tile key={r.id} row={r} />
          ))}
        </div>
      ) : null}

      <AddRefDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}
